import React, { useState, useEffect } from 'react';
import ReactPlayer from 'react-player';
import { events, events2023, events2024, events2025 } from '../data/calendar';
import { motion } from 'framer-motion';

const Calendar = () => {
  const [dynamicEvents, setDynamicEvents] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch events from Firebase or JSON endpoint
    const fetchEvents = async () => {
      try {
        // Option 1: Fetch from a JSON endpoint (you can host this on Firebase, Vercel, etc.)
        // const response = await fetch('https://your-domain.com/api/events.json');
        // const data = await response.json();

        // Option 2: Fetch from Firebase Firestore (implement getFirebaseEvents below)
        // const data = await getFirebaseEvents();

        // For now, fallback to static data
        // setDynamicEvents(data);
        setDynamicEvents(null); // Set to null to use static data
      } catch (error) {
        console.error('Failed to fetch dynamic events:', error);
        setDynamicEvents(null);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  // Use dynamic events if available, otherwise use static data
  const currentEvents2025 = dynamicEvents?.['2025'] || events2025;
  const currentEvents2024 = dynamicEvents?.['2024'] || events2024;
  const currentEvents2023 = dynamicEvents?.['2023'] || events2023;
  const currentEvents2022 = dynamicEvents?.['2022'] || events;

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.2 },
    transition: { duration: 0.6 },
  };

  return (
    <div className="bg-zinc-100 w-full flex flex-col py-20 px-6 md:px-12 lg:px-20 justify-start">
      {/* 2025 Events Section */}
      <motion.div className="mt-20" {...fadeInUp}>
        <h1 className="text-5xl md:text-6xl font-righteous text-golden mb-12">2025</h1>
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <div className="animate-pulse text-golden text-xl">Loading events...</div>
          </div>
        ) : (
          <div className="grid gap-8 md:gap-12">
            {currentEvents2025.map((event, index) => (
              <motion.div
                key={event?.what || index}
                className="bg-white p-6 md:p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow border-l-4 border-golden"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
              >
                <div className="flex items-start justify-between mb-4">
                  <h2 className="text-2xl md:text-3xl font-righteous text-gray-800">{event.what}</h2>
                  <span className="inline-block px-3 py-1 bg-golden text-white text-sm font-semibold rounded-full">
                    Upcoming
                  </span>
                </div>
                <div className="space-y-3 text-base md:text-lg leading-relaxed">
                  <p className="text-gray-700">
                    <span className="text-golden font-semibold">When: </span>
                    {event.when}
                  </p>
                  <p className="text-gray-700">
                    <span className="text-golden font-semibold">Where: </span>
                    {event.where}
                  </p>
                  {event?.about && <p className="text-gray-600 mt-3 pt-3 border-t border-gray-200">{event.about}</p>}

                  {/* Action buttons */}
                  <div className="flex flex-wrap gap-3 pt-4">
                    {event.links[0].eventLink && (
                      <a
                        href={event.links[0].eventLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-5 py-2.5 bg-golden text-white font-semibold rounded-lg hover:bg-golden/90 transform hover:scale-105 transition-all duration-300 shadow-md"
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
                          />
                        </svg>
                        Get Tickets
                      </a>
                    )}
                    {event.links[0].videoLink && (
                      <a
                        href={event.links[0].videoLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-5 py-2.5 bg-white text-golden border-2 border-golden font-semibold rounded-lg hover:bg-golden hover:text-white transform hover:scale-105 transition-all duration-300"
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        Watch Preview
                      </a>
                    )}
                    {event.links[0].umuvLink && (
                      <a
                        href={event.links[0].umuvLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-5 py-2.5 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-all duration-300"
                      >
                        View on UMUV →
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>

      {/* 2024 Events Section */}
      <motion.div className="mt-20" {...fadeInUp}>
        <h1 className="text-5xl md:text-6xl font-righteous text-golden mb-12">2024</h1>
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <div className="animate-pulse text-golden text-xl">Loading events...</div>
          </div>
        ) : (
          <div className="grid gap-8 md:gap-12">
            {currentEvents2024.map((event, index) => (
              <motion.div
                key={event?.what || index}
                className="bg-white p-6 md:p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
              >
                <h2 className="text-2xl md:text-3xl font-righteous text-gray-800 mb-4">{event.what}</h2>
                <div className="space-y-2 text-base md:text-lg leading-relaxed">
                  <p className="text-gray-700">
                    <span className="text-golden font-semibold">When: </span>
                    {event.when}
                  </p>
                  <p className="text-gray-700">
                    <span className="text-golden font-semibold">Where: </span>
                    {event.links[0].eventLink.length ? (
                      <a
                        href={event.links[0].eventLink}
                        target="_blank"
                        className="underline hover:text-golden transition-colors"
                        rel="noopener noreferrer"
                      >
                        {event.where}
                      </a>
                    ) : (
                      event.where
                    )}
                  </p>
                  {event?.about && <p className="text-gray-600 mt-3 pt-3 border-t border-gray-200">{event.about}</p>}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>
      <motion.div className="mt-20" {...fadeInUp}>
        <h1 className="text-5xl md:text-6xl font-righteous text-golden mb-12">2023</h1>
        <div className="grid gap-8 md:gap-12">
          {currentEvents2023.map((event, index) => (
            <motion.div
              key={event?.what || index}
              className="bg-white p-6 md:p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
            >
              <h2 className="text-2xl md:text-3xl font-righteous text-gray-800 mb-4">{event.what}</h2>
              <div className="space-y-2 text-base md:text-lg leading-relaxed">
                <p className="text-gray-700">
                  <span className="text-golden font-semibold">When: </span>
                  {event.when}
                </p>
                <p className="text-gray-700">
                  <span className="text-golden font-semibold">Where: </span>
                  {event.links[0].eventLink.length ? (
                    <a
                      href={event.links[0].eventLink}
                      target="_blank"
                      className="underline hover:text-golden transition-colors"
                      rel="noopener noreferrer"
                    >
                      {event.where}
                    </a>
                  ) : (
                    event.where
                  )}
                </p>
                {event?.about && <p className="text-gray-600 mt-3 pt-3 border-t border-gray-200">{event.about}</p>}
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
      <motion.div className="mt-20" {...fadeInUp}>
        <h1 className="text-5xl md:text-6xl font-righteous text-golden mb-12">2022</h1>

        <motion.img
          src="https://res.cloudinary.com/umuv/image/upload/v1666632211/Sublime%20Dance%20Company/REPERTORY/Push%20It%20%20Online/PIO-funchal.jpg"
          className="w-full md:w-1/2 lg:w-1/3 rounded-lg shadow-md mb-12"
          alt="push-it-online-cal-upcoming-show"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        />

        <div className="grid gap-8 md:gap-12 mb-12">
          {currentEvents2022.map((event, index) => (
            <motion.div
              key={event?.what || index}
              className="bg-white p-6 md:p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
            >
              <h2 className="text-2xl md:text-3xl font-righteous text-gray-800 mb-4">{event.what}</h2>
              <div className="space-y-2 text-base md:text-lg leading-relaxed">
                <p className="text-gray-700">
                  <span className="text-golden font-semibold">When: </span>
                  {event.when}
                </p>
                <p className="text-gray-700">
                  <span className="text-golden font-semibold">Where: </span>
                  {event.links[0].eventLink.length ? (
                    <a
                      href={event.links[0].eventLink}
                      target="_blank"
                      className="underline hover:text-golden transition-colors"
                      rel="noopener noreferrer"
                    >
                      {event.where}
                    </a>
                  ) : (
                    event.where
                  )}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="my-12 rounded-lg overflow-hidden shadow-lg"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <ReactPlayer url="https://youtu.be/34Gh0db6cGY" controls width="100%" height="500px" className="rounded-lg" />
        </motion.div>

        <motion.div
          className="flex flex-col md:flex-row gap-6 md:gap-10 my-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <img
            src="https://res.cloudinary.com/umuv/image/upload/v1664704884/Sublime%20Dance%20Company/REPERTORY/Kutxi%20Kutxi/kutxi-rollup-poster.jpg"
            className="w-full md:w-1/3 rounded-lg shadow-md hover:shadow-xl transition-shadow"
            alt="kutxi-poster-cal-upcoming-show"
          />
          <img
            src="https://res.cloudinary.com/umuv/image/upload/v1664705172/Sublime%20Dance%20Company/REPERTORY/Kutxi%20Kutxi/kutxi-poster.jpg"
            className="w-full md:w-2/3 rounded-lg shadow-md hover:shadow-xl transition-shadow"
            alt="kutxi-poster-cal-upcoming-show"
          />
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Calendar;
